import { BrowserModule } from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {APP_INITIALIZER, inject, NgModule} from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import {MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule, MatOptionModule} from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepperModule } from '@angular/material/stepper';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FailedComponent } from './failed/failed.component';
import {HTTP_INTERCEPTORS, HttpBackend, HttpClient, HttpClientModule} from '@angular/common/http';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent, ProtectedResourceScopes } from '@azure/msal-angular';
import { NgxPermissionsModule } from 'ngx-permissions';
import { environment } from 'src/environments/environment';
import { UserService } from './services/user.service';
import { HeaderComponent } from './menu/header/header.component';
import { VerticalBarComponent } from './menu/vertical-bar/vertical-bar.component';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatIconModule} from '@angular/material/icon';
import {MatCardModule} from '@angular/material/card';
import {ModulesService} from './services/modules.service';
import {CorporateFormsService} from './corporate/services/corporate-forms.service';
import { PopupComponent } from './utils/popup/popup.component';
import {ExpandedVerticalMenuComponent} from './menu/expanded-vertical-menu/expanded-vertical-menu.component';
import {NgOptimizedImage} from '@angular/common';
import {FooterComponent} from './menu/footer/footer.component';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatDialogModule} from '@angular/material/dialog';
import { ConfirmDialogComponent } from './corporate/confirm-dialog/confirm-dialog.component';
import {InvoicingModuleMenuComponent} from './invoicing-module/invoicing-module-menu/invoicing-module-menu.component';

import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';

import { MatBadgeModule } from '@angular/material/badge';
import { MatSnackBarModule } from '@angular/material/snack-bar'; // Ensure this is imported
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatProgressBarModule} from '@angular/material/progress-bar';

import {
  msalCustomersSignIn
} from './auth/msal/config/msal-customers.config';
import {MsalCustomerInterceptor} from './auth/msal/interceptors/msal-customer.interceptor';
import {TenantContextService} from './services/tenant-context.service';
import {CustomersLoginMsalService} from './auth/msal/services/customers-login-msal.service';
import {RedirectHandlerService} from './auth/services/test/redirect-handler.service';
import {InvoicingModuleService} from './invoicing-module/service/invoicing-module.service';
import { PlutonInvoicingModuleMenuComponent } from './pluton-invoicing-module/invoicing-module-menu/invoicing-module-menu.component';
import { PlutonInvoicingModuleService } from './pluton-invoicing-module/service/invoicing-module.service';
import { AuthRepresentativesComponent } from './corporate/documents/auth-representatives/auth-representatives.component';
import { PhonebookModule } from './corporate/phonebook';
import { MeetingNotesPanelComponent } from './corporate/meeting-notes/meeting-notes-panel/meeting-notes-panel.component';
import { AiChatNoteComponent } from './ai-chat/ai-chat-note.component';
import { NotificationDropdownComponent } from './notifications/notification-dropdown/notification-dropdown.component';
import { NotificationsPageComponent } from './notifications/notifications-page/notifications-page.component';
export function loggerCallback(logLevel: LogLevel, message: string) {
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/',
      knownAuthorities: [environment.b2cPolicies.authorityDomain]
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false
      }
    }
  });
}

export function LoginMSALCustomersInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalCustomersSignIn);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  //const protectedResourceMap = new Map<string, Array<string>>();
  const protectedResourceMap = new Map<string, Array<string | ProtectedResourceScopes> | null>();

    protectedResourceMap.set(environment.apiConfig.IdentityAPI.endpoint, [
      {
        httpMethod: 'POST',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'PUT',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'GET',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.read]
      },
      {
        httpMethod: 'DELETE',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'PATCH',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      }
    ]);

    protectedResourceMap.set(environment.apiConfig.corporateAPI.endpoint, [
      {
        httpMethod: 'POST',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'PUT',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'GET',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.read]
      },
      {
        httpMethod: 'DELETE',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'PATCH',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      }
    ]);

    protectedResourceMap.set(environment.invoicing_api, [
      {
        httpMethod: 'POST',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'PUT',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'GET',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.read]
      },
      {
        httpMethod: 'DELETE',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'PATCH',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      }
    ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      //scopes: [...environment.apiConfig.scopes],
      scopes: [],
    },
    loginFailedRoute: '/login-failed'
  };
}

@NgModule({
  declarations: [
    AppComponent,
    FailedComponent,
    HeaderComponent,
    VerticalBarComponent,
    PopupComponent,
    ConfirmDialogComponent,
    NotificationDropdownComponent,
    NotificationsPageComponent,
  ],
  imports: [
    CalendarModule.forRoot({provide: DateAdapter, useFactory: adapterFactory}),
    MatNativeDateModule,
    BrowserModule,
    MatMenuModule,
    MatDividerModule,
    MatBadgeModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    MatSnackBarModule,
    MatInputModule,
    MatButtonModule,
    MatToolbarModule,
    MatListModule,
    MatTableModule,
    MatSelectModule,
    MatOptionModule,
    MatFormFieldModule,
    MatStepperModule,
    ReactiveFormsModule,
    HttpClientModule,
    MsalModule,
    NgxPermissionsModule.forRoot(),
    MatAutocompleteModule,
    MatIconModule,
    MatCardModule,
    FormsModule,
    ExpandedVerticalMenuComponent,
    NgOptimizedImage,
    FooterComponent,
    MatTooltipModule,
    MatDialogModule,
    InvoicingModuleMenuComponent,
    PlutonInvoicingModuleMenuComponent,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    PhonebookModule,
    MeetingNotesPanelComponent,
    AiChatNoteComponent,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalCustomerInterceptor,
      multi: true,
    },
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: MsalInterceptor,
    //   multi: true,
    // },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: 'MSAL_CUSTOMERS_LOGIN',
      useFactory: LoginMSALCustomersInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      // Use this HttpClient For Anonymous Request
      provide: 'NoInterceptorHttpClient',
      useFactory: (handler: HttpBackend) => new HttpClient(handler),
      deps: [HttpBackend],
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-GB'
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: {day: 'numeric', month: 'numeric', year: 'numeric'},
        },
        display: {
          dateInput: {day: '2-digit', month: '2-digit', year: 'numeric'},
          monthYearLabel: {month: 'short', year: 'numeric'},
          dateA11yLabel: {day: 'numeric', month: 'long', year: 'numeric'},
          monthYearA11yLabel: {month: 'long', year: 'numeric'},
        },
      }
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (msalInstance: IPublicClientApplication) => {
        return () => msalInstance.initialize();
      },
      deps: [MSAL_INSTANCE],
      multi: true,
    },
    {
      // Resolve the tenant from the subdomain BEFORE the app makes any API call, so the
      // interceptor can stamp X-BC-Tenant and module gating knows what's licensed.
      provide: APP_INITIALIZER,
      useFactory: (tenantContext: TenantContextService) => {
        return () => tenantContext.bootstrap();
      },
      deps: [TenantContextService],
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    UserService,
    ModulesService,
    CorporateFormsService,
    InvoicingModuleService,
    PlutonInvoicingModuleService
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
